import { type ReactNode, useCallback } from 'react';
import * as Styled from './Link.styled';

type LinkProps = {
  children: ReactNode;
  className?: string;
  isDisabled?: boolean;
  slot?: string;
  title?: string;
} & (
  | {
      isButton?: false;
      handler?: () => void;
      href: string;
      target?: string;
    }
  | {
      isButton: true;
      handler: () => void;
      href?: never;
      target?: never;
    }
);

export const Link = ({
  children,
  handler,
  href,
  isButton = false,
  isDisabled = false,
  target,
  title,
  ...deferred
}: LinkProps) => {
  // React Aria's Link filters out the title DOM attribute via filterDOMProps,
  // so we apply it directly on the rendered element via a callback ref.
  const titleRef = useCallback(
    (el: HTMLElement | null) => {
      if (el) {
        if (title) {
          el.setAttribute('title', title);
        } else {
          el.removeAttribute('title');
        }
      }
    },
    [title],
  );

  const additionalLinkProps = target ? { rel: 'noopener', target } : {};
  return isButton ? (
    <Styled.Button isDisabled={isDisabled} onPress={handler} {...deferred}>
      {children}
    </Styled.Button>
  ) : (
    <Styled.Link
      ref={titleRef}
      href={href}
      isDisabled={isDisabled}
      onPress={handler}
      {...additionalLinkProps}
      {...deferred}
    >
      {children}
    </Styled.Link>
  );
};
